import { MAX_QUEUED_OP_AGE_MS } from '@mononoke/shared';

/**
 * The persistent op store — offline plan v1.1 §2.2 A, increment 2.
 *
 * WHAT REPLACES WHAT. `platform/uploads.ts` is a module singleton that survives
 * navigation and dies with the tab; its own comment says persisting blobs to IndexedDB
 * is "the offline increment's job". This is that job. The in-memory queue stays for the
 * INTAKE path, where a photo's destination does not exist yet — see op-queue.ts.
 *
 * ONE STORE, `pending_ops`, AND THE BLOB LIVES IN THE OP. §2.2 A is explicit that a
 * photo blob belongs in the same transaction as the op that needs it, and the reason is
 * not tidiness: a photo captured in a stockroom is not re-takeable once the garment has
 * gone back to the lender. Two stores would mean a window where an op exists with no
 * blob, or a blob with nothing pointing at it.
 *
 * THE STORE IS AN INTERFACE, INJECTED, and that is not test scaffolding. jsdom has no
 * IndexedDB and this repo has no `fake-indexeddb` — adding a production-adjacent
 * dependency to test our own logic would be the wrong trade. So the queue's LOGIC is
 * proven against `createMemoryOpStore` under vitest, and the real IndexedDB path is
 * proven where it actually exists: Playwright, in real Chromium, capturing offline and
 * replaying after a reload. Neither half is sufficient alone and both are wired.
 */

/**
 * Re-exported under this module's own name, because the queue's readers think in terms
 * of "how old may an op get". The NUMBER now lives in `@mononoke/shared`
 * (`offline/queue-window.ts`), because the SERVER derives two things from it — receipt
 * retention, and the plausibility bound on a client-supplied movement instant — and
 * increment 2 shipped it written twice with the relationship recorded only in a comment.
 * That is the shape of drift: change one, and the guarantee quietly inverts with every
 * test still passing.
 */
export const MAX_OP_AGE_MS = MAX_QUEUED_OP_AGE_MS;

/**
 * WHAT CAN BE CAPTURED OFFLINE. `photo-upload` arrived with increment 2; `movement` is
 * increment 3's headline — the plan's own example of the failure receipts exist to stop
 * ("a flaky tunnel turns one checkout into three movements").
 *
 * Every kind here must be in the SERVER's `QUEUEABLE_PROCEDURES` allowlist, or its
 * replay is refused with a 400 that no retry will fix. That coupling is deliberate: the
 * server is the authority on what may be performed against a world that has moved on,
 * and this list is only the client's half of the agreement.
 */
export type OpKind = 'photo-upload' | 'movement' | 'flag' | 'intake';

/**
 * `expired` is a REAL, VISIBLE STATE and not a synonym for deleted (G10). An op that
 * ages out is shown, ledgered and left for a human to dismiss; dropping it silently
 * would be the app quietly deciding a photo did not matter.
 */
export type OpStatus = 'queued' | 'sending' | 'failed' | 'expired';

export interface PhotoUploadPayload {
  /** Known at enqueue time — that is what makes this op replayable on its own. */
  itemId: string;
  position: number;
}

export interface MovementPayload {
  itemId: string;
  /** `out` = check-out, `in` = check-in. Spelled as the act, not as a procedure name,
   * so the store does not have to change when the API's naming does. */
  direction: 'out' | 'in';
  note?: string;
  /** The booking this movement belongs to, when it was made from a booking (frame 4d's
   * per-garment check-in). Carried through the queue so a check-in made offline still
   * lands in the booking's history on replay, exactly as an online one would. Absent for
   * a booking-less movement (the item-list bulk check-in) — the old, unchanged path. */
  bookingId?: string;
}

interface OpBase {
  /**
   * A client-minted uuid that is ALSO the idempotency key sent to the server. One
   * identity for the act, stable across every replay: minting a second key on retry
   * would defeat the receipt it is meant to match.
   */
  id: string;
  /**
   * Epoch ms. An INSTANT, not a calendar day — no date helper applies (see date.ts).
   *
   * SINCE INCREMENT 3 THIS IS ALSO EVIDENCE, not just bookkeeping: it is the capture
   * time a `movement` op sends as `occurredAt`, so a check-out recorded in a basement at
   * 17:00 says 17:00 when it reaches the server at 19:00 (§2.3, migration 017).
   */
  createdAt: number;
  attempts: number;
  status: OpStatus;
  /** The classifier's user-facing message, for the chip. Never a raw error string. */
  lastError?: string;
}

/**
 * A DISCRIMINATED UNION rather than one interface with optional fields, because `blob` is
 * required for a photo and meaningless for a movement — and an optional blob would let a
 * photo op be persisted without its bytes, which is the one thing §2.2 A says must never
 * happen.
 */
export interface FlagPayload {
  itemId: string;
  /** At least one of these; the server refuses both blank. */
  reason?: string;
  presetId?: string;
}

/**
 * An INTAKE — the only op that creates rather than modifies, and therefore the first
 * producer for the merge rule's `∪ pending-inserts` arm (read-cache.ts).
 *
 * It carries its own photos, because the whole point is that neither exists on the server
 * yet: an intake captured in a basement is an item AND its pictures, and splitting them
 * into separate ops would recreate exactly the gap increment 2 closed — a photo whose
 * destination was never created.
 */
export interface IntakePayload {
  name: string;
  brand?: string;
  description?: string;
  categoryId?: string;
  locationId?: string;
  fields?: { key: string; values: string[] }[];
}

export type PendingOp =
  | (OpBase & { kind: 'photo-upload'; payload: PhotoUploadPayload; blob: Blob })
  | (OpBase & { kind: 'movement'; payload: MovementPayload; blob?: undefined })
  // The condition photo is OPTIONAL: a flag is a sentence first, and "the hem is torn"
  // is worth recording whether or not somebody managed a picture of it.
  | (OpBase & { kind: 'flag'; payload: FlagPayload; blob?: Blob })
  | (OpBase & { kind: 'intake'; payload: IntakePayload; blobs: Blob[] });

/** What a caller hands to `enqueue` — the queue owns id, timestamps and status. */
export type NewOp =
  | { kind: 'photo-upload'; payload: PhotoUploadPayload; blob: Blob }
  | { kind: 'movement'; payload: MovementPayload }
  | { kind: 'flag'; payload: FlagPayload; blob?: Blob }
  | { kind: 'intake'; payload: IntakePayload; blobs: Blob[] };

export interface OpStore {
  put(op: PendingOp): Promise<void>;
  delete(id: string): Promise<void>;
  all(): Promise<PendingOp[]>;
}

/** Oldest first. Replay order is capture order — the strip reads as it was shot. */
const byCreatedAt = (a: PendingOp, b: PendingOp): number => a.createdAt - b.createdAt;

/** For unit tests, and for any browser where IndexedDB is unavailable (see below). */
export function createMemoryOpStore(seed: PendingOp[] = []): OpStore {
  const ops = new Map<string, PendingOp>(seed.map((op) => [op.id, op]));
  return {
    put(op) {
      ops.set(op.id, { ...op });
      return Promise.resolve();
    },
    delete(id) {
      ops.delete(id);
      return Promise.resolve();
    },
    all() {
      return Promise.resolve([...ops.values()].map((op) => ({ ...op })).sort(byCreatedAt));
    },
  };
}

export const DB_NAME = 'mononoke-ops';
export const STORE_NAME = 'pending_ops';
const DB_VERSION = 1;

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB request failed'));
    };
  });
}

/**
 * The real store. Opened lazily and once — an `open` per operation would serialise
 * every write behind a fresh connection handshake, and a version change while a
 * connection is open blocks until it closes.
 */
export function createIndexedDbOpStore(dbName: string = DB_NAME): OpStore {
  let connection: Promise<IDBDatabase> | undefined;

  const open = (): Promise<IDBDatabase> => {
    connection ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error ?? new Error('could not open the offline queue database'));
      };
      // Firefox in private mode and Safari with storage disabled reject the open
      // rather than throwing synchronously. The caller turns this into a visible
      // refusal; it must never look like an empty queue.
      request.onblocked = () => {
        reject(new Error('the offline queue database is blocked by another tab'));
      };
    });
    return connection;
  };

  /**
   * A transaction is awaited on its COMPLETE event, not on the request's success.
   * A request can succeed inside a transaction that then aborts — resolving on the
   * request would report a durable write that never landed, which is precisely the
   * silent failure this queue exists to remove.
   */
  const write = async (run: (store: IDBObjectStore) => void): Promise<void> => {
    const db = await open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => {
        resolve();
      };
      tx.onerror = () => {
        reject(tx.error ?? new Error('offline queue write failed'));
      };
      tx.onabort = () => {
        reject(tx.error ?? new Error('offline queue write was aborted'));
      };
      run(tx.objectStore(STORE_NAME));
    });
  };

  return {
    async put(op) {
      await write((store) => store.put(op));
    },
    async delete(id) {
      await write((store) => store.delete(id));
    },
    async all() {
      const db = await open();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const ops = await promisify(tx.objectStore(STORE_NAME).getAll() as IDBRequest<PendingOp[]>);
      return ops.sort(byCreatedAt);
    },
  };
}
